import { BaseExplorerConfig, ColumnConfig, QuickFilter } from '../../../types/baseExplorerConfig';
import { gridColumns, ColumnSchema } from '../../../shared/columnSchema';
import { dateTimeInNwt } from '../../../utils/nwtDate';
import { airframeCode } from '../../../shared/affAirframe';
import type { FieldTransformer } from '../../../types/baseExplorerConfig';
import {
  AFF_AIRCRAFT_COLUMNS,
  AFF_TRACK_COLUMNS,
  AFF_AIRCRAFT_SORT_COLUMNS,
  AFF_TRACK_SORT_COLUMNS,
  affSortable,
} from '../../../shared/affSchema';
import {
  affAircraftAdapter,
  affAircraftAdapterWithSim,
  affTracksAdapter,
  affTracksAdapterWithSim,
  AffAircraftEntity,
  AffTrackEntity,
} from './affServiceAdapter';
import { dispatchAffLocatePoint } from './affTrackEvent';

export interface AffConfigOptions {
  /**
   * #866 — Export CSV shows only for `aff` + `export`. Defaults to false so a
   * caller that forgets to ask the permission system offers no export.
   */
  canExport?: boolean;
  /** List simulated aircraft too (aff.simulate holders, for testing). */
  includeSim?: boolean;
}

export interface AffTracksConfigHandlers extends AffConfigOptions {
  /** Called with the row's esn so the aircraft can be fetched in full. */
  onShowAircraft?: (esn: string) => void;
  onLocate?: (row: AffTrackEntity) => void;
}

/** Aircraft reported stale by AFF are dimmed rather than hidden — absence is information. */
const isStale = (entity: AffAircraftEntity): boolean => entity.stale === true;

/**
 * #798 — Observed and Captured are timestamps and must read as NWT local date
 * and time in the grid as well as the panels. The grid renders through
 * HOTDataGrid, which applies these per field, so the formatting hangs here.
 *
 * Values arrive as ISO strings from the grid API and as epoch milliseconds from
 * the live map layer; dateTimeInNwt takes either. An absent timestamp becomes an
 * empty cell rather than an epoch date, which would read as a real observation.
 */
const nwtTimestampTransformer: FieldTransformer = {
  transform: (value: string | number | null | undefined) => dateTimeInNwt(value) ?? '',
};

const AFF_TIMESTAMP_TRANSFORMERS = {
  positionTimestamp: nwtTimestampTransformer,
  createdAt: nwtTimestampTransformer,
};

/**
 * #866 — a track row carries the aircraft as flown (make, model, owner,
 * function, type from the history period covering the point). The type reads
 * FW/RW as it does on the Aircraft grid, where the server abbreviates it.
 */
const AFF_TRACK_TRANSFORMERS = {
  ...AFF_TIMESTAMP_TRANSFORMERS,
  resourceSubclass: { transform: (value: string | null | undefined) => airframeCode(value) ?? '' },
};

/**
 * #866 U1 — the column filters each grid actually supports, and nothing else.
 *
 * The CBE only opens a filter panel for a field listed in quickFilters, so a
 * column marked filterable without an entry here shows a filter icon that does
 * nothing. The grid's filterable flag is therefore derived from these lists
 * (see withFilterableFrom) rather than taken from the schema, and every field
 * here must be translated by affServiceAdapter into a key the backend reads.
 *
 * Time filters are dateRange only: From/To become gte/lte, the only operators
 * the adapter can express.
 */
export const AFF_AIRCRAFT_QUICK_FILTERS: QuickFilter[] = [
  { field: 'name', label: 'Tail', type: 'textSearch' },
  { field: 'esn', label: 'ESN/IMEI (exact)', type: 'textSearch' },
  { field: 'callsign', label: 'Call Sign', type: 'textSearch' },
  { field: 'make', label: 'Make', type: 'textSearch' },
  { field: 'model', label: 'Model', type: 'textSearch' },
  { field: 'owner', label: 'Owner', type: 'textSearch' },
  { field: 'operationalFunction', label: 'Function (exact)', type: 'textSearch' },
  { field: 'groupDesignator', label: 'Group (exact)', type: 'textSearch' },
  {
    field: 'stale',
    label: 'Stale',
    type: 'dropdown',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
];

export const AFF_TRACK_QUICK_FILTERS: QuickFilter[] = [
  { field: 'name', label: 'Tail', type: 'textSearch' },
  { field: 'callsign', label: 'Call Sign', type: 'textSearch' },
  { field: 'esn', label: 'ESN/IMEI (exact)', type: 'textSearch' },
  // Setting either end replaces the grid's default 24 h window (affFilters).
  { field: 'positionTimestamp', label: 'Observed', type: 'dateRange' },
  { field: 'createdAt', label: 'Captured', type: 'dateRange' },
];

/**
 * A column shows a filter icon exactly when it has a filter panel to open, and
 * a sort arrow exactly when the router accepts it as sortBy (#866 U1, U2).
 */
const columnsFor = (
  schema: ColumnSchema[],
  filters: QuickFilter[],
  sortColumns: Readonly<Record<string, string>>,
): ColumnConfig[] =>
  gridColumns(schema).map((c) => ({
    ...c,
    filterable: filters.some((f) => f.field === c.field),
    sortable: affSortable(sortColumns, c.field),
  }));

export const createAffAircraftConfig = (
  { canExport = false, includeSim = false }: AffConfigOptions = {},
): BaseExplorerConfig<AffAircraftEntity> => ({
  entityName: 'Aircraft',
  complexityLevel: 'moderate',
  // Positions are upserted continuously by the collector.
  autoRefreshIntervalMs: 60000,

  displayFields: AFF_AIRCRAFT_COLUMNS.map((c) => c.field),

  getRowClassName: (entity: AffAircraftEntity) => (isStale(entity) ? 'aff-stale-aircraft' : undefined),

  serviceAdapter: includeSim ? affAircraftAdapterWithSim : affAircraftAdapter,

  fieldTransformers: AFF_TIMESTAMP_TRANSFORMERS,

  features: {
    pagination: {
      enabled: true,
      defaultPageSize: 50,
      pageSizeOptions: [25, 50, 100, 200],
      showPageInfo: true,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
      defaultSort: [{ field: 'name', direction: 'asc' }],
    },
    searching: {
      enabled: true,
      placeholder: 'Search by tail, ESN, make, model or owner...',
      searchFields: ['name', 'esn', 'make', 'model', 'owner'],
      debounceMs: 300,
    },
    filtering: {
      enabled: true,
      advancedFilters: false,
      quickFilters: AFF_AIRCRAFT_QUICK_FILTERS,
    },
    editing: {
      // AFF is collected from the aircraft themselves — never edited here.
      enabled: false,
      allowCreate: false,
      allowUpdate: false,
      allowDelete: false,
      mode: 'modal',
    },
    export: {
      enabled: canExport,
      formats: ['csv', 'shapefile', 'geojson', 'kml'],
      includeFilters: true,
      entity: 'aff_aircraft',
      spatialEndpoint: '/api/aff/aircraft/download',
    },
  },

  presentation: {
    title: 'Aircraft',
    subtitle: 'Current aircraft positions from Automated Flight Following',
    columnConfig: columnsFor(AFF_AIRCRAFT_COLUMNS, AFF_AIRCRAFT_QUICK_FILTERS, AFF_AIRCRAFT_SORT_COLUMNS),
    styling: {
      theme: 'default',
      striped: true,
      bordered: true,
      hoverable: true,
    },
    layout: {
      responsive: true,
      showFooter: true,
      // Gates the whole header-actions block in ConfigurableBaseExplorer —
      // context switcher, Export CSV and help all hang off this.
      showToolbar: true,
      minHeight: 400,
      maxHeight: 800,
    },
  },
});

export const createAffTracksConfig = (
  handlers: AffTracksConfigHandlers = {},
): BaseExplorerConfig<AffTrackEntity> => ({
  entityName: 'Aircraft Track',
  complexityLevel: 'moderate',

  displayFields: AFF_TRACK_COLUMNS.map((c) => c.field),

  serviceAdapter: handlers.includeSim ? affTracksAdapterWithSim : affTracksAdapter,

  fieldTransformers: AFF_TRACK_TRANSFORMERS,

  features: {
    pagination: {
      enabled: true,
      defaultPageSize: 50,
      pageSizeOptions: [25, 50, 100, 200],
      showPageInfo: true,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
      // Observation time, not capture time.
      defaultSort: [{ field: 'positionTimestamp', direction: 'desc' }],
    },
    searching: {
      enabled: true,
      placeholder: 'Search by tail or ESN...',
      searchFields: ['name', 'esn'],
      debounceMs: 300,
    },
    filtering: {
      enabled: true,
      advancedFilters: false,
      quickFilters: AFF_TRACK_QUICK_FILTERS,
    },
    editing: {
      enabled: false,
      allowCreate: false,
      allowUpdate: false,
      allowDelete: false,
      mode: 'modal',
    },
    export: {
      enabled: handlers.canExport ?? false,
      formats: ['csv', 'shapefile', 'geojson', 'kml'],
      includeFilters: true,
      entity: 'aff_tracks',
      spatialEndpoint: '/api/aff/tracks/download',
    },
    customActions: {
      enabled: true,
      actions: [
        {
          id: 'locate',
          label: 'Locate',
          icon: '<i class="fa-solid fa-crosshairs"></i>',
          className: 'btn btn-sm btn-outline',
          // The unique thing a track row can answer: where this aircraft was
          // at this moment. Nothing else in the app puts you on a specific
          // historical position.
          onClick: (row: AffTrackEntity) => {
            if (handlers.onLocate) {
              handlers.onLocate(row);
              return;
            }
            dispatchAffLocatePoint({
              lat: Number(row.lat),
              lon: Number(row.lon),
              name: row.name,
              esn: String(row.esn),
              // #866 U5 — the marker label is read by people in NWT (#798).
              observed: dateTimeInNwt(row.positionTimestamp) ?? undefined,
            });
          },
          isVisible: () => true,
          isEnabled: (row: AffTrackEntity) => row?.lat != null && row?.lon != null,
        },
        {
          id: 'aircraft',
          label: 'Aircraft',
          icon: '<i class="fa-solid fa-plane"></i>',
          className: 'btn btn-sm btn-outline',
          // By esn, not from the row: the modal is the aircraft now (position,
          // activity calendar, track actions), which a history row cannot give.
          onClick: (row: AffTrackEntity) => handlers.onShowAircraft?.(String(row.esn)),
          isVisible: () => true,
          isEnabled: (row: AffTrackEntity) => !!row?.esn,
        },
      ],
    },
  },

  presentation: {
    title: 'Aircraft Tracks',
    subtitle: 'Aircraft position history by observation time',
    columnConfig: columnsFor(AFF_TRACK_COLUMNS, AFF_TRACK_QUICK_FILTERS, AFF_TRACK_SORT_COLUMNS),
    styling: {
      theme: 'default',
      striped: true,
      bordered: true,
      hoverable: true,
    },
    layout: {
      responsive: true,
      showFooter: true,
      // Gates the whole header-actions block in ConfigurableBaseExplorer —
      // context switcher, Export CSV and help all hang off this.
      showToolbar: true,
      minHeight: 400,
      maxHeight: 800,
    },
  },
});
